import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormHelperText, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography, } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useSnackbar } from 'notistack';

import { getAllWarehousesState } from '../../../../features/inventory/get_all_warehouses';
import { getWarehouseName } from '../../../common/utils/warehouse_utils';
import { WarehouseTypeEnum } from '../../../../core/enums/inventory/warehouse';
import { isEmptyObject } from '../../../../core/utils/misc_utils';

const AddWarehouseDestinationDialog = ({ formik, sourceWarehouseId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const getAllWarehouses = useSelector(getAllWarehousesState);

    const [open, setOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [showMore, setShowMore] = useState(false);

    const onClose = () => setOpen(false);

    const onAddDestination = (destination) => {
        const destinationsList = formik?.values?.destinationsList || [];
        const lastItem = destinationsList[destinationsList.length - 1];
        const index = destinationsList.findIndex(d => d?.warehouseId === destination?.value);

        if (index === -1) {
            const newDestinations = [
                ...destinationsList,
                {
                    position: destinationsList.length ? lastItem.position + 1 : 0,
                    name: destination.label,
                    destinationPodId: undefined,
                    warehouseId: destination.value,
                    warehouseType: destination.type,
                }
            ];
            formik.setFieldValue('destinationsList', newDestinations);
            onClose();
        } else {
            enqueueSnackbar('Lokalizacja już dodana', { variant: 'error' });
        }
    };

    const filterTextLower = filterText.toLowerCase();

    const defaultOptions = useMemo(() => {
        return getAllWarehouses?.data
            ?.filter(wh => wh?.internalId !== sourceWarehouseId)
            ?.filter(wh => [WarehouseTypeEnum.MAIN.value, WarehouseTypeEnum.INVENTORY.value].includes(wh?.type) || (wh?.type === WarehouseTypeEnum.POD.value && wh?.pod?.serviceId != null))
            ?.map(wh => ({
                value: wh?.internalId,
                label: getWarehouseName(wh),
                type: wh?.type,
            }))
            ?.filter(opt => !filterText || opt.label.toLowerCase().includes(filterTextLower));
    }, [getAllWarehouses?.data, filterText, filterTextLower, sourceWarehouseId]);

    const extraOptions = useMemo(() => {
        return getAllWarehouses?.data
            ?.filter(wh => wh?.internalId !== sourceWarehouseId)
            ?.filter(wh => ![WarehouseTypeEnum.MAIN.value, WarehouseTypeEnum.INVENTORY.value].includes(wh?.type) && !(wh?.type === WarehouseTypeEnum.POD.value && wh?.pod?.serviceId != null))
            ?.map(wh => ({
                value: wh?.internalId,
                label: getWarehouseName(wh),
                type: wh?.type,
            }))
            ?.filter(opt => !filterText || opt.label.toLowerCase().includes(filterTextLower));
    }, [getAllWarehouses?.data, filterText, filterTextLower, sourceWarehouseId]);

    const renderOption = (opt) => (
        <ListItem
            key={ opt.value }
            button
            onClick={ () => onAddDestination(opt) }
            sx={ {
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: 'slate.100',
                },
            } }
        >
            <ListItemIcon>
                { opt.type === WarehouseTypeEnum.POD.value ? <StorefrontIcon/> : <WarehouseIcon/> }
            </ListItemIcon>
            <ListItemText
                primary={ opt.label }
            />
        </ListItem>
    );

    return (
        <>
            <Box sx={ { width: '300px', mt: 4 } } onClick={ () => setOpen(true) }>
                <Stack direction="row" alignItems="center" gap={ 1 } sx={ { cursor: 'pointer' } }>
                    <AddCircleIcon color="primary"/>
                    <Typography fontWeight="bold">Dodaj kolejną lokalizację</Typography>
                </Stack>
                <FormHelperText error>
                    { !isEmptyObject(formik?.errors?.destinationsList) && formik?.errors?.destinationsList }
                </FormHelperText>
            </Box>

            <Dialog
                open={ open }
                onClose={ onClose }
                fullWidth
                maxWidth={ false }
                PaperProps={ {
                    sx: {
                        width: '800px',
                        height: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }
                } }
            >
                <DialogTitle>Wybierz Magazyn</DialogTitle>

                <Box sx={ { px: 2, pb: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 } }>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Szukaj..."
                        value={ filterText }
                        onChange={ (e) => setFilterText(e.target.value) }
                    />
                </Box>

                <DialogContent sx={ { flex: 1, overflowY: 'auto' } }>
                    <List>
                        { defaultOptions.map(renderOption) }

                        { extraOptions.length > 0 && !showMore && (
                            <ListItemButton
                                onClick={ () => setShowMore(true) }
                                sx={ { justifyContent: 'center' } }
                            >
                                ➕ Pokaż więcej
                            </ListItemButton>
                        ) }

                        { showMore && extraOptions.map(renderOption) }

                        { showMore && extraOptions.length > 0 && (
                            <ListItemButton
                                onClick={ () => setShowMore(false) }
                                sx={ { justifyContent: 'center' } }
                            >
                                ➖ Pokaż mniej
                            </ListItemButton>
                        ) }
                    </List>
                </DialogContent>

                <Divider/>

                <DialogActions sx={ { px: 3, py: 2 } }>
                    <Button onClick={ onClose } variant="outlined">Zamknij</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddWarehouseDestinationDialog;
